// Used in reports-filter.component.html and filter-in-view.component.html

// Angular
import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    AfterViewInit,
} from "@angular/core";
import { MatCalendar } from "@angular/material/datepicker";

// Services/Types/Helpers
import { DateFormatterHelper } from "#helpers/date-formatter.helper";
import { TimezoneHelper } from "#helpers/timezone.helper";
import { PPUser as User } from "cli-types";
import { DateRanges } from "#models/filterParams.interface";

// Moment
import moment from "moment";

// RXJS/Store
import { Subject } from "rxjs";

@Component({
    selector: "date-ranges-filter-menu",
    templateUrl: "./date-ranges-filter-menu.component.html",
    styleUrls: ["./date-ranges-filter-menu.component.scss"],
    standalone: false
})
export class DateRangesFilterMenuComponent
    implements OnInit, OnDestroy, AfterViewInit
{
    fromDate: any;
    toDate: any;
    lastSevenDaysActive: boolean;
    thisWeekActive: boolean;
    overdueActive: boolean;
    dueTodayActive: boolean;
    datesMismatch: boolean;
    setOverdueStatusFilter: boolean;
    applyButtonText: string;
    selectedQuickOption: string | null;
    initialFromDate: Date;
    initialToDate: Date;

    private ngUnsubscribe = new Subject();

    @Input() classStartDate: moment.Moment | string;
    @Input() savedDateRanges: DateRanges | undefined;
    @Input() userData: User;
    @Input() onReports: boolean;
    @Input() hideDuePresets: boolean;
    @Input() hideDueTodayPreset: boolean;
    @Input() hideOverduePreset: boolean;
    @Input() hideThisWeekPreset: boolean;
    @Input() showLastSevenDaysPreset: boolean;
    @Input() dueDateStatus: string | null;

    @Output() sendDateRanges = new EventEmitter<any>();

    @ViewChild("fromCalendar", { static: false })
    fromCalendar: MatCalendar<Date>;
    @ViewChild("toCalendar", { static: false }) toCalendar: MatCalendar<Date>;

    constructor(
        private dateFormatterHelper: DateFormatterHelper,
        private timezoneHelper: TimezoneHelper
    ) {}

    ngOnInit(): void {
        this.thisWeekActive = false;
        this.overdueActive = false;
        this.dueTodayActive = false;
        this.applyButtonText = "Apply";
        if (this.onReports) {
            this.applyButtonText = "Select Dates";
        }
    }
    ngAfterViewInit() {
        if (this.savedDateRanges) {
            this.fromDate = new Date(this.savedDateRanges.from);
            this.toDate = new Date(this.savedDateRanges.to);
            this.initialFromDate = this.fromDate;
            this.initialToDate = this.toDate;
            this.changeMonth(this.fromDate, this.toDate);
        }
        if (this.dueDateStatus) {
            this.selectedQuickOption = this.dueDateStatus;
            if (this.dueDateStatus === "overdue") {
                this.overdueActive = true;
            } else if (this.dueDateStatus === "dueThisWeek") {
                this.thisWeekActive = true;
            } else if (this.dueDateStatus === "dueToday") {
                this.dueTodayActive = true;
            } else if (this.dueDateStatus === "lastSevenDays") {
                this.lastSevenDaysActive = true;
            }
        }
    }

    onFromDateChange(event: Date) {
        this.fromDate = event;
        this.datesMismatch = this.fromDate > this.toDate;

        if (this.dueTodayActive) {
            this.dueTodayActive = false;
            this.dueDateStatus = null;
            this.selectedQuickOption = null;
        } else if (
            this.thisWeekActive &&
            this.checkIfNotDueThisWeek(this.fromDate, this.toDate)
        ) {
            this.thisWeekActive = false;
            this.dueDateStatus = null;
            this.selectedQuickOption = null;
        }
    }
    onToDateChange(event: Date) {
        this.toDate = event;
        this.datesMismatch = this.fromDate > this.toDate;

        if (
            this.overdueActive &&
            this.checkIfNotOverdue(this.toDate, this.getYesterday())
        ) {
            this.overdueActive = false;
            this.dueDateStatus = null;
            this.selectedQuickOption = null;
        } else if (this.dueTodayActive) {
            this.dueTodayActive = false;
            this.dueDateStatus = null;
            this.selectedQuickOption = null;
        } else if (
            this.thisWeekActive &&
            this.checkIfNotDueThisWeek(this.fromDate, this.toDate)
        ) {
            this.thisWeekActive = false;
            this.dueDateStatus = null;
            this.selectedQuickOption = null;
        }
    }

    checkIfNotOverdue(to: Date, yesterday: Date) {
        if (to > yesterday) {
            return true;
        }
    }
    checkIfNotDueThisWeek(from: Date, to: Date) {
        let dueThisWeekFrom = this.getThisWeekFrom();
        let dueThisWeekTo = this.getThisWeekTo();

        dueThisWeekFrom = new Date(
            new Date(dueThisWeekFrom).setHours(0, 0, 0, 0)
        );
        from = new Date(new Date(from).setHours(0, 0, 0, 0));
        dueThisWeekTo = new Date(
            new Date(dueThisWeekTo).setHours(23, 59, 59, 0)
        );
        to = new Date(new Date(to).setHours(23, 59, 59, 0));

        if (from < dueThisWeekFrom || to > dueThisWeekTo) {
            return true;
        }
    }
    checkIfLastSevenDays(from: Date, to: Date) {
        const fromDate = from;
        fromDate.setHours(0, 0, 0, 0);
        const toDate = to;
        toDate.setHours(0, 0, 0, 0);
        const nowDate = new Date();
        nowDate.setHours(0, 0, 0, 0);
        const millisecondsPerDay = 24 * 60 * 60 * 1000;
        const daysBetween =
            (<any>this.treatAsUTC(toDate) - <any>this.treatAsUTC(fromDate)) /
            millisecondsPerDay;
        if (daysBetween === 7 && toDate.getUTCDate() === nowDate.getUTCDate()) {
            return true;
        }
    }
    treatAsUTC(date: Date) {
        const result = new Date(date);
        result.setMinutes(result.getMinutes() - result.getTimezoneOffset());
        return result;
    }

    // Set From and To, to today's date
    setDueToday() {
        this.fromDate = new Date(new Date().setHours(0, 0, 0, 0)); // start of the current day
        this.toDate = new Date(new Date().setHours(23, 59, 59, 0)); // end of the current day
        this.changeMonth(this.fromDate, this.toDate);
        this.thisWeekActive = false;
        this.overdueActive = false;
        this.dueTodayActive = true;
        this.setOverdueStatusFilter = false;
        this.selectedQuickOption = "dueToday";
        this.datesMismatch = false;
    }

    // Get Overdue (first day of class (plus an additional six months) through yesterday
    getOverdueDates() {
        this.fromDate = this.classStartDate;
        this.fromDate = new Date(this.fromDate);
        this.fromDate.setMonth(this.fromDate.getMonth() - 6); // backdate by six months for assurance
        this.toDate = this.getYesterday();
        this.changeMonth(this.fromDate, this.toDate);
        this.thisWeekActive = false;
        this.overdueActive = true;
        this.dueTodayActive = false;
        this.setOverdueStatusFilter = true;
        this.selectedQuickOption = "overdue";
        this.datesMismatch = false;
    }

    // The start date is Monday of the current week, the end date is the following Sunday.
    setThisWeekDates() {
        this.fromDate = this.getThisWeekFrom();
        this.toDate = this.getThisWeekTo();
        this.changeMonth(this.fromDate, this.toDate);
        this.thisWeekActive = true;
        this.overdueActive = false;
        this.dueTodayActive = false;
        this.setOverdueStatusFilter = false;
        this.selectedQuickOption = "dueThisWeek";
        this.datesMismatch = false;
    }

    setLastSevenDaysDates() {
        this.fromDate = this.getLastSevenDaysFrom();
        this.toDate = new Date();
        this.changeMonth(this.fromDate, this.toDate);
        this.lastSevenDaysActive = true;
        this.thisWeekActive = false;
        this.overdueActive = false;
        this.dueTodayActive = false;
        this.setOverdueStatusFilter = false;
        this.selectedQuickOption = "dueThisWeek";
        this.datesMismatch = false;
    }

    getThisWeekFrom() {
        const today = new Date();
        const dayOfWeek = today.getDay(); // Sunday = 0, Saturday = 6

        // get start of week
        if (dayOfWeek === 0) {
            return today;
        } else {
            let daysToSubtract = 0;
            let x = dayOfWeek;
            for (const i = 0; i < x; x--) {
                daysToSubtract++;
            }
            const startOfWeek = today.setDate(
                today.getDate() - (daysToSubtract - 1)
            );
            return new Date(startOfWeek);
        }
    }
    getThisWeekTo() {
        const today = new Date();
        const dayOfWeek = today.getDay(); // Sunday = 0, Saturday = 6
        // get end of week
        if (dayOfWeek === 6) {
            return today;
        } else {
            let daysToAdd = 0;
            let x = dayOfWeek;
            for (const i = 6; i > x; x++) {
                daysToAdd++;
            }
            const endOfWeek = today.setDate(today.getDate() + (daysToAdd + 1));
            return new Date(endOfWeek);
        }
    }
    getLastSevenDaysFrom() {
        const today = new Date();
        const from = today.setDate(today.getDate() - 7);
        return new Date(from);
    }

    // Reset to default date values
    resetDates() {
        let fDate = new Date();
        const tDate = new Date();
        this.fromDate = null;
        // toDate is set to undefined and not null because null is considered 0 and will
        // mess up the logic to the datesMismatch warning message
        this.toDate = undefined;
        this.savedDateRanges = undefined;
        this.changeMonth(fDate, tDate);
        this.thisWeekActive = false;
        this.overdueActive = false;
        this.dueTodayActive = false;
        this.datesMismatch = false;
        this.setOverdueStatusFilter = false;
    }

    // get Tomorrow's date
    getTomorrow() {
        const today = new Date();
        const tomorrow = today.setDate(today.getDate() + 1);
        return new Date(tomorrow);
    }

    // get Yesterday's date
    getYesterday() {
        const today = new Date();
        const yesterday = today.setDate(today.getDate() - 1);
        return new Date(new Date(yesterday).setHours(23, 59, 59, 0)); // end of yesterday
    }

    formatDatesForServer(date: Date): string {
        return this.timezoneHelper.convertUserDateToServerTZ(
            moment(date).format("YYYY-MM-DD HH:mm:ss"),
            this.userData.timezone
        );
    }
    formatDate(date: string) {
        return this.dateFormatterHelper.formatDateMMDDYY(date);
    }

    changeMonth(fromDate: Date, toDate: Date) {
        this.fromCalendar._goToDateInView(fromDate, "month");
        this.toCalendar._goToDateInView(toDate, "month");
    }

    applyDates() {
        let dates: {} | null = {};
        if (this.fromDate && this.toDate) {
            const fromDate: Date = new Date(
                new Date(this.fromDate).setHours(0, 0, 0, 0)
            );
            const toDate: Date = new Date(
                new Date(this.toDate).setHours(23, 59, 59, 0)
            );
            const formattedFromDate = this.formatDatesForServer(fromDate);
            const formattedToDate = this.formatDatesForServer(toDate);
            dates = [
                { from: formattedFromDate, to: formattedToDate },
                { selectedQuickOption: this.selectedQuickOption },
            ];
        } else {
            dates = null;
        }
        this.sendDateRanges.emit(dates);
    }

    // Disable Functions
    disableResetButton() {
        if (this.lastSevenDaysActive) {
            return true;
        }
    }
    disableSelectButton() {
        if ((!this.toDate || !this.fromDate) && !this.savedDateRanges) {
            return true;
        } else if (this.datesMismatch) {
            return true;
        } else if (
            // If the initially set dates match the newly picked dates, disable
            this.initialToDate?.setHours(0, 0, 0, 0) ===
                this.toDate?.setHours(0, 0, 0, 0) &&
            this.initialFromDate?.setHours(0, 0, 0, 0) ===
                this.fromDate?.setHours(0, 0, 0, 0)
        ) {
            return true;
        }
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next(true);
        this.ngUnsubscribe.complete();
    }
}
